import React, { FC, useEffect, useRef, useState } from 'react'
import { useForm } from 'react-hook-form'
import { createUseStyles } from 'react-jss'
import { useAtom } from 'jotai'
import moment from 'moment'
import {
    Calendar,
    EditFill,
    IcAddJob,
    IcMoveJob,
    Info,
    SendFill,
    SuitcaseFill,
} from '../../../assets/icons'
import { Button } from '../../ui/Button/Button'
import {
    ITransferMemberToOtherWorkplace,
    IUpdateMemberData,
    Member,
} from '../../../models/Member'
import { CircleIcon, Circle } from '../../ui/Circle/Circle'
import { Typography } from '../../ui/Typography/Typography'
import { Overlay } from '../../ui/Sidebar'
import { DrawerHeaderMemberInfo } from '../FormElements/DrawerHeader'
import { FormHeader } from '../../ui/FormElememts/FormHeader'
import { FormControl } from '../../ui/FormElememts/FormControl'
import {
    FormInputNumber,
    FormSelect,
    FormTextArea,
} from '../FormElements/FormInput'
import { Workplace } from '../../../models/Workplace'
import { getWorkplacesAndPositions } from '../../../api/misc'
import { Position } from '../../../models/Position'
import { FormMessage } from '../../ui/FormElememts/FormMessage'
import { L, L_HTML } from '../../providers/Lokalise'
import { DatePickerComponent } from '../../ui/FormElememts/DatePicker'
import { colors } from '../../../theme/palette'
import { profileAtom } from '../../../api/profile/profile'
import { transferMemberToOtherWorkplace } from '../../../api/members/members'
import { ImitatorNoAccessWarning } from '../Imitator/ImitatorNoAccessWarning'
import { Dialog } from '../../ui/Dialog/Dialog'

const useStyles = createUseStyles({
    contentWrapper: {
        padding: '100px 20px 20px',
        overflow: 'auto',
        height: 'calc(100% - 100px)',
        paddingBottom: '80px',
    },
    bottom: {
        display: 'flex',
        position: 'absolute',
        bottom: '0',
        width: '100%',
        padding: '20px',
        left: '0',
        background: colors.white,
        zIndex: 4,
        borderTop: `1px solid ${colors.borderColor}`,
        '& > button': {
            flexGrow: 1,
        },
        '& > button:last-of-type': {
            marginRight: '0',
        },
    },
})

interface TransferMemberToOtherWorkplaceProps {
    member: Member
    onOpen?: () => void
}

export type RegistrationFormFields = {
    GammeltArbejdsstednummer: string
    NytArbejdsstednummer: string
    Stillingsnummer: string
    Timetal: string
    OevrigeOplysninger: string
}

export type CustomErrors = {
    Aendringsdato?: boolean
}

const TransferMemberToOtherWorkplace: FC<
    TransferMemberToOtherWorkplaceProps
> = ({ member, onOpen }) => {
    const [workPlaces, setWorkplaces] = useState<Workplace[]>([])
    const { register, formState, reset, handleSubmit, setValue } =
        useForm<RegistrationFormFields>()
    const [selectedDate, _setSelectedDate] = useState(null)
    const [selectedWorkplace, setSelectedWorkplace] = useState<
        string | undefined
    >(undefined)
    const [selectedPosition, setSelectedPosition] = useState<
        string | undefined
    >(undefined)
    const [positions, setPositions] = useState<Position[]>([])
    const [isOpen, setIsOpen] = useState(false)
    const classes = useStyles()
    const { dirtyFields, errors } = formState
    const [customErrors, setCustomErrors] = useState<CustomErrors>({})
    const [authProfile] = useAtom(profileAtom)
    const [showImitatorModal, setShowImitatorModal] = useState(false)
    const [showDialog, setShowDialog] = useState(false)

    const getPositions = async (): Promise<void> => {
        const response = await getWorkplacesAndPositions()
        let workplaces = response.workPlaces

        workplaces = workplaces.filter((place) => {
            return member.workPlaces.some(
                (work) => work.number !== place.number
            )
        })

        setWorkplaces(workplaces)
    }

    const resetForm = (): void => {
        setIsOpen(false)
    }

    const submitHandler = async (
        data: RegistrationFormFields
    ): Promise<string> => {
        if (authProfile.isImitator) {
            setShowImitatorModal(true)
            return Promise.resolve('OK')
        }

        if (!selectedDate) {
            setCustomErrors({
                Aendringsdato: true,
            })
            return Promise.resolve('OK')
        }

        const fieldsToEdit: ITransferMemberToOtherWorkplace = {
            Medlemsident: member.memberId,
            Aendringsdato: moment(selectedDate).format('YYYY-MM-DD HH:mm:ss'),
            NytArbejdsstednummer: Number(data.NytArbejdsstednummer),
            GammeltArbejdsstednummer: Number(data.GammeltArbejdsstednummer),
            Stillingsnummer: Number(data.Stillingsnummer),
        }
        if (dirtyFields.Timetal) {
            fieldsToEdit.Timetal = Number(
                String(data.Timetal).replace(',', '.')
            )
        }

        if (dirtyFields.OevrigeOplysninger) {
            fieldsToEdit.OevrigeOplysninger = data.OevrigeOplysninger
        }

        const response = await transferMemberToOtherWorkplace(fieldsToEdit)

        if (response) {
            setIsOpen(false)
            setShowDialog(true)
            resetForm()
        }

        return Promise.resolve('OK')
    }

    const setWP = (wp: string): void => {
        const found = workPlaces.find((wpp) => String(wpp.number) === wp)

        setSelectedWorkplace(wp)
        setSelectedPosition('default')

        if (found && found.positions) {
            setPositions(found.positions)
        }
    }

    useEffect(() => {
        getPositions()
    }, [])

    return (
        <>
            <Button
                type="action"
                onClick={() => {
                    setIsOpen(true)
                    if (onOpen) {
                        onOpen()
                    }
                }}
                style={{
                    width: '100%',
                    display: 'flex',
                    alignContent: 'start',
                    padding: 0,
                    justifyContent: 'left',
                }}
            >
                <Circle marginRight=".5rem">
                    <CircleIcon iconWidth="22px">
                        <IcMoveJob />
                    </CircleIcon>
                </Circle>
                <Typography tag="h3" size="XS" spacing=".2rem">
                    {L(
                        'MINEMEDLEMMER_DETALJE_TILFØJ-OVERFØR-ARBEJDSPLADS_KNAP'
                    )}
                </Typography>
            </Button>
            <Overlay
                isShowing={isOpen}
                hide={() => {
                    setIsOpen(false)
                    resetForm()
                }}
            >
                <DrawerHeaderMemberInfo member={member} />
                <form
                    noValidate
                    className={classes.contentWrapper}
                    onSubmit={handleSubmit(submitHandler)}
                >
                    <FormHeader>
                        <div
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                flexDirection: 'column',
                                justifyContent: 'center',
                                width: '100%',
                            }}
                        >
                            <div style={{ marginBottom: '1rem' }}>
                                <Circle marginRight=".5rem">
                                    <CircleIcon>
                                        <IcMoveJob />
                                    </CircleIcon>
                                </Circle>
                            </div>
                            <Typography tag="h3" size="M" spacing=".2rem">
                                {L(
                                    'MINEMEDLEMMER_OVERFØR-TIL-ANDET-ARBEJDSSTED_FORMHEADER_LABEL_1'
                                )}
                            </Typography>
                        </div>
                    </FormHeader>
                    <FormControl>
                        <FormSelect
                            register={register}
                            required
                            name="GammeltArbejdsstednummer"
                            hasError={
                                !!errors?.GammeltArbejdsstednummer?.message
                            }
                            rules={{
                                required: 'Arbejssted er påkrævet!',
                            }}
                            isOpen={isOpen}
                            placeholder="Overfør fra *"
                            options={member.workPlaces.map((wp) => {
                                const o = {
                                    label: wp.name,
                                    value: String(wp.number),
                                }

                                return o
                            })}
                        />
                        <FormMessage
                            text=""
                            error={errors?.GammeltArbejdsstednummer?.message}
                        />
                    </FormControl>
                    <FormControl>
                        <FormSelect
                            register={register}
                            required
                            name="NytArbejdsstednummer"
                            hasError={!!errors?.NytArbejdsstednummer?.message}
                            rules={{
                                required: 'Arbejssted er påkrævet!',
                                onChange: (e) => {
                                    setWP(e.target.value)
                                },
                            }}
                            isOpen={isOpen}
                            placeholder="Overfør til *"
                            options={workPlaces.map((wp) => {
                                const o = {
                                    label: wp.name,
                                    value: String(wp.number),
                                }

                                return o
                            })}
                        />
                        <FormMessage
                            text=""
                            error={errors?.NytArbejdsstednummer?.message}
                        />
                    </FormControl>
                    <FormControl>
                        <FormSelect
                            register={register}
                            required
                            name="Stillingsnummer"
                            hasError={!!errors?.Stillingsnummer?.message}
                            rules={{
                                required: 'Stilling er påkrævet!',
                            }}
                            isOpen={isOpen}
                            placeholder="Stilling *"
                            options={positions.map((pos) => {
                                const o = {
                                    label: pos.name,
                                    value: String(pos.id),
                                }

                                return o
                            })}
                        />
                        <FormMessage
                            text={L('GENERISK_FORM_TIMETAL_VALIDERINGS_TEKST')}
                            error={errors?.Stillingsnummer?.message}
                        />
                    </FormControl>
                    <FormControl>
                        <FormInputNumber
                            label={`${L(
                                'REDIGER-MEDLEM_FORM_TIMETAL_LABEL'
                            )} *`}
                            name="Timetal"
                            required={false}
                            useFloats
                            register={register}
                            defaultValue={undefined}
                            hasError={!!errors?.Timetal?.message}
                            rules={{
                                validate: (value) =>
                                    (Number(String(value).replace(',', '.')) >=
                                        0.5 &&
                                        Number(
                                            String(value).replace(',', '.')
                                        ) <= 99) ||
                                    L(
                                        'GENERISK_FORM_TIMETAL_VALIDERINGS_TEKST'
                                    ),
                            }}
                        />
                        <FormMessage
                            text={L('GENERISK_FORM_TIMETAL_VALIDERINGS_TEKST')}
                            error={errors?.Timetal?.message}
                        />
                    </FormControl>
                    <FormHeader>
                        <Circle marginRight=".5rem">
                            <CircleIcon iconWidth="18px">
                                <Calendar />
                            </CircleIcon>
                        </Circle>
                        <Typography tag="h3" size="M" spacing=".2rem">
                            {L(
                                'MINEMEDLEMMER_OVERFØR-TIL-ANDET-ARBEJDSSTED_FORMHEADER_LABEL_2'
                            )}
                        </Typography>
                    </FormHeader>
                    <FormControl>
                        <DatePickerComponent
                            label={`${L('GENERISK_FORM_ÆNDRES-PR_LABEL')}`}
                            selected={selectedDate}
                            setSelected={(e) => {
                                _setSelectedDate(e)
                                setCustomErrors({})
                            }}
                            hasError={!!customErrors?.Aendringsdato}
                            minDate={moment().subtract(1, 'year').toDate()}
                            maxDate={moment().add(1, 'year').toDate()}
                        />
                        <FormMessage
                            text=""
                            error={
                                customErrors.Aendringsdato
                                    ? 'Ændringsdato påkrævet'
                                    : undefined
                            }
                        />
                    </FormControl>
                    <FormHeader>
                        <Circle marginRight=".5rem">
                            <CircleIcon iconWidth="22px">
                                <Info />
                            </CircleIcon>
                        </Circle>
                        <Typography tag="h3" size="M" spacing=".2rem">
                            {L(
                                'MINEMEDLEMMER_OVERFØR-TIL-ANDET-ARBEJDSSTED_FORMHEADER_LABEL_3'
                            )}
                        </Typography>
                    </FormHeader>
                    <FormControl>
                        <FormTextArea
                            label={L('GENERISK_FORM_ØVRIGE-OPLYSNINGER_LABEL')}
                            register={register}
                            name="OevrigeOplysninger"
                            required={false}
                        />
                    </FormControl>
                    <div className={classes.bottom}>
                        <Button
                            onClick={() => {
                                setIsOpen(false)
                                resetForm()
                            }}
                            type="secondary"
                            size="large"
                        >
                            Annuller
                        </Button>
                        <Button
                            buttonType="submit"
                            type="primary"
                            size="large"
                            onClick={() => {
                                if (!selectedDate) {
                                    setCustomErrors({
                                        Aendringsdato: true,
                                    })
                                }
                            }}
                        >
                            {L_HTML(
                                'MINEMEDLEMMER_OVERFØR-ARBEJDSSTED_OVERFØR_LABEL'
                            )}
                        </Button>
                    </div>
                </form>
            </Overlay>
            {showDialog ? (
                <Dialog
                    trigger={null}
                    defaultOpen
                    hideClose
                    icon={<SendFill width="46px" />}
                >
                    <Typography
                        style={{ marginBottom: '30px' }}
                        size="L"
                        align="center"
                    >
                        {L_HTML(
                            'OVERFØR-MEDLEM-TIL-ARBEJDSPLADS_POPUP_OVERSKRIFT'
                        )}
                    </Typography>
                    <Typography
                        tag="p"
                        size="S"
                        style={{ marginBottom: '30px' }}
                        align="center"
                    >
                        {L_HTML('OVERFØR-MEDLEM-TIL-ARBEJDSPLADS_POPUP_TEXT')}
                    </Typography>
                    <Button size="xlarge" onClick={() => setShowDialog(false)}>
                        {L_HTML('OVERFØR-MEDLEM-TIL-ARBEJDSPLADS_POPUP_KNAP')}
                    </Button>
                </Dialog>
            ) : null}
            <ImitatorNoAccessWarning
                show={showImitatorModal}
                onClose={() => setShowImitatorModal(false)}
            />
        </>
    )
}

export { TransferMemberToOtherWorkplace }
